/**
 * P&L Summary — Power BI-style dashboard for MYOB financials.
 *
 * Layout follows Darren's Power BI reference:
 *   • FY filter dropdown (FY27 / FY26 / FY25 …)
 *   • Left column: BU table with Budget, Sales, COGS breakdown, GP, GM %
 *   • Right column: Sales to Budget Ratio bar chart
 *   • Full-width: Gross Margin % over time (current FY vs LY)
 *   • Bottom strip: Budget upload area (drag-and-drop CSV)
 */
import { useEffect, useMemo, useState, Fragment } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  LineChart, Line, XAxis, YAxis, Tooltip as ReTooltip, ResponsiveContainer,
  Legend, ReferenceLine, BarChart, Bar, LabelList, CartesianGrid,
} from "recharts";
import { Loader2, Upload, RefreshCw, Trash2, TrendingUp, FileDown, Info, Stethoscope, Download, X, ChevronRight, ChevronDown } from "lucide-react";

const TIDS = {
  fySelect: "pnl-summary-fy-select",
  refresh: "pnl-summary-refresh-btn",
  budgetUpload: "pnl-summary-budget-upload",
  budgetUploadBtn: "pnl-summary-budget-upload-btn",
  budgetFY: (fy) => `pnl-summary-budget-fy-${fy}`,
  budgetDelete: (fy) => `pnl-summary-budget-delete-${fy}`,
  actualsUpload: "pnl-summary-actuals-upload",
  actualsUploadBtn: "pnl-summary-actuals-upload-btn",
  actualsFY: (fy) => `pnl-summary-actuals-fy-${fy}`,
  actualsDelete: (fy) => `pnl-summary-actuals-delete-${fy}`,
  buRow: (code) => `pnl-summary-bu-row-${code}`,
};

const fmt = (v) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 })
    .format(v || 0);
const fmtK = (v) => `$${((v || 0) / 1000).toFixed(0)}K`;
// Closing stock is stored negative (contra-COGS). The subtraction happens in
// the COGS/GP maths; here we just show the magnitude as a plain positive figure.
const fmtKContra = (v) => (v || 0) < 0 ? `($${(Math.abs(v) / 1000).toFixed(0)}K)` : fmtK(v);
const fmtKAbs = (v) => fmtK(Math.abs(v || 0));
const fmtPct = (v) => `${(v || 0).toFixed(1)}%`;
const monthLabel = (yyyymm) => {
  if (!yyyymm) return "";
  const [y, m] = yyyymm.split("-");
  const names = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${names[parseInt(m,10)-1]}-${y.slice(-2)}`;
};

// Parent/child roll-up by BU code band: anything 2xx rolls under 200, 4xx under
// 400, etc. A band total = the sum of every BU in that band (including the 'x00'
// unit itself). BUs with no numeric code (e.g. ADM) stand alone.
const SUM_FIELDS = ["budget_sales_target", "sales", "opening_stock", "materials",
  "direct_wages", "subcontractors", "labour_hire", "closing_stock", "cogs",
  "gross_profit", "sales_ly", "cogs_ly"];
const bandOf = (code) => {
  const m = String(code || "").match(/\d{3,}/);
  if (!m) return null;
  return Math.floor(parseInt(m[0].slice(0, 3), 10) / 100) * 100;
};

// The 12 numeric P&L cells, reused for single / parent-band / child rows.
const numCells = (r) => (
  <>
    <td className="px-3 py-2 text-right tabular-nums text-slate-600">{fmtK(r.budget_sales_target)}</td>
    <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtK(r.sales)}</td>
    <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmtK(r.opening_stock)}</td>
    <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmtK(r.materials)}</td>
    <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmtK(r.direct_wages)}</td>
    <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmtK(r.subcontractors)}</td>
    <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmtK(r.labour_hire)}</td>
    <td className="px-3 py-2 text-right tabular-nums text-slate-500" title="Deducted from COGS — stock still on hand">{fmtKAbs(r.closing_stock)}</td>
    <td className="px-3 py-2 text-right tabular-nums text-slate-600">{fmtKContra(r.cogs)}</td>
    <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtK(r.gross_profit)}</td>
    <td className={`px-3 py-2 text-right tabular-nums font-medium ${r.gross_margin_pct < 25 ? "text-rose-700" : r.gross_margin_pct < 35 ? "text-amber-700" : "text-emerald-700"}`}>{fmtPct(r.gross_margin_pct)}</td>
    <td className="px-3 py-2 text-right tabular-nums text-slate-500">{fmtPct(r.gross_margin_pct_ly)}</td>
  </>
);

const FY_OPTIONS = () => {
  // Current AU FY: Jul-Jun. If today is July+ we're in FY(year+1).
  const now = new Date();
  const y = now.getFullYear();
  const currentFy = now.getMonth() >= 6 ? y + 1 : y;
  return [currentFy, currentFy - 1, currentFy - 2, currentFy - 3, currentFy - 4]
    .map((fy) => ({ value: fy, label: `FY${fy % 100}` }));
};


const FY_MONTHS = (fyYear) => {
  // Jul → Jun in FY order
  const startYear = fyYear - 1;
  const out = [];
  for (let i = 0; i < 12; i++) {
    const mIdx = 6 + i;   // 6 = July
    const y = startYear + Math.floor(mIdx / 12);
    const m = (mIdx % 12) + 1;
    out.push({
      key: `${y.toString().padStart(4,"0")}-${m.toString().padStart(2,"0")}`,
      label: monthLabel(`${y}-${m.toString().padStart(2,"0")}`),
    });
  }
  return out;
};


export default function PLSummaryPanel({ canEdit = true }) {
  const [fy, setFy] = useState(FY_OPTIONS()[0].value);
  const [selectedMonths, setSelectedMonths] = useState([]);  // empty = all
  const [hiddenBUs, setHiddenBUs] = useState(() => {
    try { return JSON.parse(localStorage.getItem("gbe_pl_hidden_bus") || "[]"); }
    catch { return []; }
  });
  const [showAllocationHelp, setShowAllocationHelp] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [diagOpen, setDiagOpen] = useState(false);
  const [budgetSummaries, setBudgetSummaries] = useState([]);
  const [actualsSummaries, setActualsSummaries] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingActuals, setUploadingActuals] = useState(false);
  const [drillTarget, setDrillTarget] = useState(null);   // {bu_id, bu_code, month}
  const [expandedBands, setExpandedBands] = useState(() => new Set());

  const load = async () => {
    setBusy(true);
    try {
      const params = { fy_year: fy, rollup: false };
      if (selectedMonths.length > 0 && selectedMonths.length < 12) {
        params.months = selectedMonths.join(",");
      }
      const [pl, bs, as_] = await Promise.all([
        api.get(`/myob/budgets/pl-summary`, { params }),
        api.get(`/myob/budgets/summary`).catch(() => ({ data: { fy_summaries: [] } })),
        api.get(`/myob/budgets/pl-actuals/summary`).catch(() => ({ data: { fy_summaries: [] } })),
      ]);
      setData(pl.data);
      setBudgetSummaries(bs.data.fy_summaries || []);
      setActualsSummaries(as_.data.fy_summaries || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load P&L Summary");
    } finally {
      setBusy(false);
    }
  };

  // Reload on FY or month-selection change
  useEffect(() => { load(); }, [fy, selectedMonths]);   // eslint-disable-line

  // Reset month selection when FY changes
  useEffect(() => { setSelectedMonths([]); }, [fy]);

  const toggleMonth = (mkey) => {
    setSelectedMonths((prev) =>
      prev.includes(mkey) ? prev.filter((m) => m !== mkey) : [...prev, mkey]
    );
  };
  const selectAllMonths = () => setSelectedMonths([]);
  const selectYTD = () => {
    // Current calendar month key
    const now = new Date();
    const curKey = `${now.getFullYear()}-${(now.getMonth()+1).toString().padStart(2,"0")}`;
    const months = FY_MONTHS(fy).map((m) => m.key);
    // Include months up to and including current (or all if FY is prior)
    const upTo = months.indexOf(curKey);
    if (upTo < 0) setSelectedMonths([]);   // FY in future → select all
    else setSelectedMonths(months.slice(0, upTo + 1));
  };

  const uploadFiles = async (fileList, endpoint, setLoader, label) => {
    if (!fileList || !fileList.length) return;
    setLoader(true);
    let ok = 0;
    for (const f of fileList) {
      const form = new FormData();
      form.append("file", f);
      try {
        const { data } = await api.post(endpoint, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success(`Imported ${label} ${data.fy_label}: ${data.accounts_imported} accounts × ${data.months_covered.length}mo`);
        ok++;
      } catch (e) {
        toast.error(`${f.name}: ${e.response?.data?.detail || "import failed"}`);
      }
    }
    setLoader(false);
    if (ok > 0) load();
  };

  const onUpload = (fl) => uploadFiles(fl, "/myob/budgets/import", setUploading, "Budget");
  const onUploadActuals = (fl) => uploadFiles(fl, "/myob/budgets/pl-actuals/import", setUploadingActuals, "P&L Actuals");

  const deleteFY = async (fyYear) => {
    if (!confirm(`Delete all budget rows for FY${fyYear % 100}?`)) return;
    try {
      await api.delete(`/myob/budgets/fy/${fyYear}`);
      toast.success(`FY${fyYear % 100} budget cleared`);
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  const deleteActualsFY = async (fyYear) => {
    if (!confirm(`Delete P&L Actuals for FY${fyYear % 100}?`)) return;
    try {
      await api.delete(`/myob/budgets/pl-actuals/fy/${fyYear}`);
      toast.success(`FY${fyYear % 100} actuals cleared`);
      load();
    } catch (e) { toast.error("Delete failed"); }
  };

  const allRows = data?.rows || [];
  const rows = useMemo(
    () => allRows.filter((r) => !hiddenBUs.includes(r.bu_code)),
    [allRows, hiddenBUs]
  );
  // Recompute total on client whenever filters change so hidden BUs don't inflate
  const total = useMemo(() => {
    if (allRows.length === 0) return data?.total || {};
    if (hiddenBUs.length === 0) return data?.total || {};
    const keys = ["budget_sales_target","sales","opening_stock","materials",
                  "direct_wages","subcontractors","labour_hire","closing_stock",
                  "cogs","gross_profit"];
    const t = { bu_code: "Total (filtered)", bu_name: "Group Total (filtered)" };
    keys.forEach((k) => { t[k] = rows.reduce((s, r) => s + (r[k] || 0), 0); });
    t.gross_margin_pct = t.sales > 0
      ? +((t.gross_profit / t.sales) * 100.0).toFixed(2)
      : 0;
    t.sales_vs_budget_ratio = t.budget_sales_target > 0
      ? +(t.sales / t.budget_sales_target).toFixed(2)
      : 0;
    // Preserve LY totals from BE (they're group-level, not per BU here)
    t.sales_ly = data?.total?.sales_ly;
    t.cogs_ly = data?.total?.cogs_ly;
    t.gross_margin_pct_ly = data?.total?.gross_margin_pct_ly;
    return t;
  }, [rows, allRows.length, data?.total, hiddenBUs]);
  const trend = data?.gm_trend || [];

  // Group filtered rows into code bands (200s, 400s, …). A band with >1 member
  // becomes an expandable parent (band total); a lone member stays a flat row.
  const grouped = useMemo(() => {
    const bands = new Map();
    const singles = [];
    rows.forEach((r) => {
      const b = bandOf(r.bu_code);
      if (b == null) { singles.push(r); return; }
      if (!bands.has(b)) bands.set(b, []);
      bands.get(b).push(r);
    });
    const out = [];
    Array.from(bands.keys()).sort((a, b) => a - b).forEach((b) => {
      const kids = bands.get(b);
      if (kids.length === 1) { out.push({ type: "single", row: kids[0] }); return; }
      const parentRow = kids.find((r) => {
        const m = String(r.bu_code || "").match(/\d{3,}/);
        return m && parseInt(m[0].slice(0, 3), 10) === b;
      });
      const agg = { bu_code: String(b), bu_name: parentRow?.bu_name || `${b} group`, band: b };
      SUM_FIELDS.forEach((k) => { agg[k] = kids.reduce((s, r) => s + (r[k] || 0), 0); });
      agg.gross_margin_pct = agg.sales > 0 ? +((agg.gross_profit / agg.sales) * 100).toFixed(2) : 0;
      agg.sales_vs_budget_ratio = agg.budget_sales_target > 0 ? +(agg.sales / agg.budget_sales_target).toFixed(2) : 0;
      agg.gross_margin_pct_ly = agg.sales_ly > 0 ? +(((agg.sales_ly - agg.cogs_ly) / agg.sales_ly) * 100).toFixed(2) : null;
      out.push({ type: "group", band: b, parent: agg, children: [...kids].sort((x, y) => String(x.bu_code).localeCompare(String(y.bu_code))) });
    });
    singles.forEach((r) => out.push({ type: "single", row: r }));
    return out;
  }, [rows]);

  const toggleBand = (b) => setExpandedBands((prev) => {
    const next = new Set(prev);
    next.has(b) ? next.delete(b) : next.add(b);
    return next;
  });

  const drillButton = (r) => (
    <button
      type="button"
      onClick={() => setDrillTarget({
        bu_id: r.bu_id, bu_code: r.bu_code, bu_name: r.bu_name,
        month: (selectedMonths.length ? [...selectedMonths].sort().slice(-1)[0]
                                       : (data?.months || []).slice(-1)[0]?.key),
      })}
      className="text-left hover:underline hover:text-gbe cursor-pointer"
      data-testid={`pnl-bu-drill-${r.bu_code}`}
      title="Click to see underlying invoices & bills"
    >
      {r.bu_code}
      <div className="text-[9px] text-slate-400 font-normal">{r.bu_name}</div>
    </button>
  );

  const toggleBU = (code) => {
    setHiddenBUs((prev) => {
      const next = prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code];
      try { localStorage.setItem("gbe_pl_hidden_bus", JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const showAllBUs = () => {
    setHiddenBUs([]);
    try { localStorage.setItem("gbe_pl_hidden_bus", "[]"); } catch {}
  };

  const budgetRatioChartData = useMemo(() =>
    rows.map((r) => ({
      code: r.bu_code || "",
      ratio: r.sales_vs_budget_ratio || 0,
    })).filter((r) => r.ratio > 0), [rows]);

  const trendChartData = useMemo(() =>
    trend.map((t) => ({
      month: monthLabel(t.period_month),
      "GM %": t.gm_pct_fy,
      "GM % LY": t.gm_pct_ly,
      "Sales FY": t.sales_fy,
      "Sales LY": t.sales_ly,
      "Materials": t.materials_fy || 0,
      "Materials LY": t.materials_ly || 0,
    })), [trend]);
  const hasMaterials = useMemo(
    () => trendChartData.some((r) => (r.Materials || 0) > 0 || (r["Materials LY"] || 0) > 0),
    [trendChartData]);

  return (
    <div className="space-y-4">
      {/* Header strip */}
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 tracking-tight">P&amp;L Summary</h3>
          <p className="text-xs text-slate-500">
            BU-level actuals vs budget · reads live MYOB data · matches Power BI layout
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(fy)} onValueChange={(v) => setFy(parseInt(v, 10))}>
            <SelectTrigger className="h-8 w-28 text-xs" data-testid={TIDS.fySelect}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FY_OPTIONS().map((o) => (
                <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={load} disabled={busy}
                  data-testid={TIDS.refresh} className="text-xs">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
          <Button size="sm" variant="outline"
                  onClick={() => setDiagOpen(true)}
                  data-testid="pnl-classifier-diagnostics-btn"
                  className="text-xs"
                  title="See which MYOB accounts landed in which cost bucket — use if a column (esp. Materials) shows $0.">
            <Stethoscope className="w-3.5 h-3.5 mr-1" />
            Diagnose
          </Button>
          <Button size="sm" variant="outline"
                  onClick={() => {
                    const monthsQP = selectedMonths.length ? `&months=${selectedMonths.join(",")}` : "";
                    api.get(`/myob/budgets/pl-summary/export.xlsx?fy_year=${fy}${monthsQP}`,
                            { responseType: "blob" })
                      .then((r) => {
                        const blob = new Blob([r.data], {
                          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                        });
                        const objUrl = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = objUrl;
                        // Extract filename from Content-Disposition if present
                        const cd = r.headers?.["content-disposition"] || "";
                        const m = /filename="?([^"]+)"?/.exec(cd);
                        a.download = m ? m[1] : `pl-summary-FY${String(fy).slice(-2)}.xlsx`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        URL.revokeObjectURL(objUrl);
                      });
                  }}
                  data-testid="pnl-export-xlsx-btn"
                  className="text-xs"
                  title="Download the P&L Summary matrix as XLSX (mirrors the on-screen table + trend + coverage sheets).">
            <Download className="w-3.5 h-3.5 mr-1" />
            Export
          </Button>
          <Button size="sm" variant="outline"
                  onClick={() => {
                    const monthsQP = selectedMonths.length ? `&months=${selectedMonths.join(",")}` : "";
                    api.get(`/myob/budgets/pl-summary/export.pdf?fy_year=${fy}${monthsQP}`,
                            { responseType: "blob" })
                      .then((r) => {
                        const blob = new Blob([r.data], { type: "application/pdf" });
                        const objUrl = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = objUrl;
                        const cd = r.headers?.["content-disposition"] || "";
                        const m = /filename="?([^"]+)"?/.exec(cd);
                        a.download = m ? m[1] : `pl-summary-FY${String(fy).slice(-2)}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        URL.revokeObjectURL(objUrl);
                      });
                  }}
                  data-testid="pnl-export-pdf-btn"
                  className="text-xs"
                  title="Download a branded PDF of the P&L Summary for board packs. Numbers guaranteed to match the on-screen table.">
            <Download className="w-3.5 h-3.5 mr-1" />
            PDF
          </Button>
        </div>
      </div>

      <ClassifierDiagnosticsModal fy={fy}
        open={diagOpen}
        onClose={() => setDiagOpen(false)} />

      <PLDrilldownModal target={drillTarget}
        fyMonths={(data?.months || []).map((m) => m.key)}
        onClose={() => setDrillTarget(null)}
        onMonthChange={(newMonth) => setDrillTarget((t) => t && { ...t, month: newMonth })} />

      {/* Month multi-selector */}
      <div className="flex items-center gap-2 flex-wrap bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
        <span className="text-[11px] font-medium text-slate-600 whitespace-nowrap">
          {selectedMonths.length === 0 ? `Months: All ${data?.fy_label || `FY${fy % 100}`}` : `${selectedMonths.length} month${selectedMonths.length > 1 ? "s" : ""}`}
        </span>
        <div className="flex items-center gap-1 flex-wrap ml-1">
          {FY_MONTHS(fy).map((m) => {
            const active = selectedMonths.length === 0 || selectedMonths.includes(m.key);
            return (
              <button key={m.key} type="button"
                      onClick={() => toggleMonth(m.key)}
                      data-testid={`pnl-summary-month-${m.key}`}
                      className={`text-[10px] px-2 py-0.5 rounded border ${
                        active ? (selectedMonths.length === 0 ? "bg-slate-200 border-slate-300 text-slate-700" : "bg-gbe text-white border-gbe")
                               : "bg-white border-slate-200 text-slate-400 hover:bg-slate-100"
                      }`}>
                {m.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-1 ml-auto">
          <button type="button" onClick={selectAllMonths}
                  data-testid="pnl-summary-months-all"
                  className="text-[10px] text-slate-600 hover:text-slate-900 underline">
            All 12
          </button>
          <button type="button" onClick={selectYTD}
                  data-testid="pnl-summary-months-ytd"
                  className="text-[10px] text-slate-600 hover:text-slate-900 underline">
            YTD
          </button>
        </div>
      </div>

      {/* Coverage callouts */}
      {data && !data.coverage.budget_loaded && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          No budget loaded for {data.fy_label}. Upload a MYOB Budget Management CSV below to unlock budget vs actual comparisons.
        </div>
      )}
      {data && !data.coverage.pl_actuals_loaded && (
        <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded px-3 py-2">
          <b>Approximate cost splits</b> — COGS breakdowns below are derived from year-to-date account balances (not FY-specific).
          Upload a MYOB Profit &amp; Loss CSV (Reports → Accounts → Profit &amp; Loss, monthly view) for accurate per-month GM% and cost splits.
        </div>
      )}
      {data && data.coverage.pl_actuals_loaded && (
        <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
          ✅ P&amp;L Actuals loaded for {data.fy_label}. Cost splits and GM% trend are FY-accurate.
        </div>
      )}

      {/* BU filter + Allocation explainer */}
      {allRows.length > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] font-medium text-slate-600 whitespace-nowrap">
              {hiddenBUs.length === 0 ? `BUs: All ${allRows.length}` : `${allRows.length - hiddenBUs.length}/${allRows.length} shown`}
            </span>
            <div className="flex items-center gap-1 flex-wrap">
              {allRows.map((r) => {
                const hidden = hiddenBUs.includes(r.bu_code);
                return (
                  <button key={r.bu_code} type="button"
                    onClick={() => toggleBU(r.bu_code)}
                    data-testid={`pnl-bu-toggle-${r.bu_code}`}
                    className={`px-2 py-0.5 text-[10px] rounded border transition ${
                      hidden
                        ? "bg-white border-slate-200 text-slate-400 line-through"
                        : "bg-gbe text-white border-gbe"
                    }`}>
                    {r.bu_code}
                  </button>
                );
              })}
            </div>
            {hiddenBUs.length > 0 && (
              <button type="button" onClick={showAllBUs}
                data-testid="pnl-bu-show-all"
                className="text-[10px] text-slate-600 hover:text-slate-900 underline ml-auto">
                Show all
              </button>
            )}
            <button type="button" onClick={() => setShowAllocationHelp((v) => !v)}
              data-testid="pnl-bu-allocation-help"
              className="text-[10px] text-slate-600 hover:text-slate-900 underline whitespace-nowrap flex items-center gap-1">
              <Info className="w-3 h-3" />
              {showAllocationHelp ? "Hide" : "What is BU Allocation?"}
            </button>
          </div>
          {showAllocationHelp && (
            <div className="mt-2 pt-2 border-t border-slate-200 text-[11px] text-slate-700 leading-relaxed">
              <b className="text-slate-900">BU Allocation — how it works</b>
              <ul className="mt-1 ml-4 list-disc space-y-1">
                <li>Every <b>Income</b> and <b>Cost of Sales</b> account in MYOB is mapped to a Business Unit via
                  <span className="font-mono bg-white border border-slate-200 px-1 rounded mx-1">MYOB → BU Auto-suggest</span>
                  (based on the account's Job Tag or code prefix).</li>
                <li><b>Sales per BU</b> = the BU's mapped income-account share of the group total (weighted by MYOB current-balance).</li>
                <li><b>COGS per BU</b> = the mapped cost-of-sales rows for that BU, split into Materials / Direct Wages / Subcontractors / Labour Hire / Opening &amp; Closing Stock via account-name pattern matching.</li>
                <li>Hidden BUs above are <b>excluded from the Total row</b> — Sales, COGS and Gross Margin % all re-compute against only the visible BUs.</li>
                <li>Filters persist per-user (stored locally).</li>
              </ul>
            </div>
          )}
        </div>
      )}

      {/* BU table + Budget ratio chart */}
      <div className="grid grid-cols-1 xl:grid-cols-[3fr_1fr] gap-4">
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left font-medium px-3 py-2">BU</th>
                <th className="text-right font-medium px-3 py-2">Budget Sales</th>
                <th className="text-right font-medium px-3 py-2">Sales</th>
                <th className="text-right font-medium px-3 py-2">Opening Stock</th>
                <th className="text-right font-medium px-3 py-2">Materials</th>
                <th className="text-right font-medium px-3 py-2">Direct Wages</th>
                <th className="text-right font-medium px-3 py-2">Subcontractors</th>
                <th className="text-right font-medium px-3 py-2">Labour Hire</th>
                <th className="text-right font-medium px-3 py-2">Closing Stock</th>
                <th className="text-right font-medium px-3 py-2">COGS</th>
                <th className="text-right font-medium px-3 py-2">GP</th>
                <th className="text-right font-medium px-3 py-2">GM %</th>
                <th className="text-right font-medium px-3 py-2">GM % LY</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((g) => {
                if (g.type === "single") {
                  const r = g.row;
                  return (
                    <tr key={r.bu_id} data-testid={TIDS.buRow(r.bu_code)}
                        className="border-b border-slate-100 hover:bg-slate-50/60">
                      <td className="px-3 py-2 font-mono font-medium text-slate-800">
                        {drillButton(r)}
                      </td>
                      {numCells(r)}
                    </tr>
                  );
                }
                const open = expandedBands.has(g.band);
                const p = g.parent;
                return (
                  <Fragment key={`band-${g.band}`}>
                    <tr data-testid={`pnl-summary-band-row-${g.band}`}
                        onClick={() => toggleBand(g.band)}
                        className="border-b border-slate-100 bg-slate-50/50 hover:bg-slate-100/70 cursor-pointer">
                      <td className="px-3 py-2 font-mono font-semibold text-slate-900">
                        <button type="button" className="flex items-center gap-1 text-left"
                                data-testid={`pnl-summary-band-toggle-${g.band}`}>
                          {open ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                                : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />}
                          {p.bu_code}
                          <span className="ml-1 text-[9px] font-sans font-normal text-slate-500">
                            ({g.children.length}) {p.bu_name}
                          </span>
                        </button>
                      </td>
                      {numCells(p)}
                    </tr>
                    {open && g.children.map((r) => (
                      <tr key={r.bu_id} data-testid={TIDS.buRow(r.bu_code)}
                          className="border-b border-slate-100 bg-white hover:bg-slate-50/60">
                        <td className="px-3 py-2 pl-8 font-mono text-slate-700">
                          {drillButton(r)}
                        </td>
                        {numCells(r)}
                      </tr>
                    ))}
                  </Fragment>
                );
              })}
              {/* Total row */}
              {rows.length > 0 && (
                <tr className="bg-slate-50 font-semibold border-t-2 border-slate-300">
                  <td className="px-3 py-2 text-slate-900">Total</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtK(total.budget_sales_target)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtK(total.sales)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtK(total.opening_stock)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtK(total.materials)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtK(total.direct_wages)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtK(total.subcontractors)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtK(total.labour_hire)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtKAbs(total.closing_stock)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtKContra(total.cogs)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtK(total.gross_profit)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${total.gross_margin_pct < 25 ? "text-rose-700" : total.gross_margin_pct < 35 ? "text-amber-700" : "text-emerald-700"}`}>
                    {fmtPct(total.gross_margin_pct)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-500" title={total.sales_ly ? `LY Sales ${fmtK(total.sales_ly)} · LY COGS ${fmtK(total.cogs_ly)}` : "No LY P&L Actuals loaded"}>
                    {total.gross_margin_pct_ly ? fmtPct(total.gross_margin_pct_ly) : "—"}
                  </td>
                </tr>
              )}
              {rows.length === 0 && !busy && (
                <tr>
                  <td colSpan={13} className="px-3 py-8 text-center text-xs text-slate-400">
                    No mapped BUs for {data?.fy_label || "this FY"}. Go to MYOB → Auto-suggest to map accounts to BUs.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Sales to Budget Ratio */}
        <div className="bg-white border border-slate-200 rounded-lg p-3">
          <div className="text-xs font-semibold text-slate-700 mb-2">Sales to Budget Ratio</div>
          {budgetRatioChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={budgetRatioChartData} layout="vertical" margin={{ top: 4, right: 30, left: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="code" tick={{ fontSize: 10 }} width={40} />
                <ReferenceLine x={1} stroke="#94a3b8" strokeDasharray="4 4" />
                <ReTooltip formatter={(v) => v.toFixed(2)} />
                <Bar dataKey="ratio" fill="#1e293b" radius={[0, 4, 4, 0]}>
                  <LabelList dataKey="ratio" position="right" formatter={(v) => v.toFixed(2)} style={{ fontSize: 10 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-[11px] text-slate-400 text-center py-8">Load budget to compare ratio</div>
          )}
        </div>
      </div>

      {/* GM % over time */}
      <div className="bg-white border border-slate-200 rounded-lg p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
            <TrendingUp className="w-3.5 h-3.5" /> Gross Margin % + Materials Cost · {data?.fy_label} vs LY
          </div>
          <div className="text-[10px] text-slate-400">
            {hasMaterials ? "Materials cost from P&L Actuals (left axis $)" : "Materials line hidden — no P&L Actuals loaded"} · GM % right axis
          </div>
        </div>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={trendChartData} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="month" tick={{ fontSize: 10 }} />
            <YAxis yAxisId="pct" orientation="right" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} domain={[0, 60]} />
            <YAxis yAxisId="mat" orientation="left" tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}K`} />
            <ReTooltip formatter={(v, name) => name.startsWith("GM") ? `${v}%` : fmt(v)} />
            <Legend wrapperStyle={{ fontSize: 10 }} />
            <Line yAxisId="pct" type="monotone" dataKey="GM %"    stroke="#0f172a" strokeWidth={2} dot={{ r: 3 }} />
            <Line yAxisId="pct" type="monotone" dataKey="GM % LY" stroke="#eab308" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="4 4" />
            {hasMaterials && (
              <Line yAxisId="mat" type="monotone" dataKey="Materials" stroke="#ea580c" strokeWidth={2} dot={{ r: 3 }} name="Materials" />
            )}
            {hasMaterials && (
              <Line yAxisId="mat" type="monotone" dataKey="Materials LY" stroke="#fdba74" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="4 4" name="Materials LY" />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Budget upload strip */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
              <FileDown className="w-4 h-4" /> Budget Management CSV import
            </div>
            <div className="text-[11px] text-slate-500">
              Upload one CSV per FY exported from MYOB (Reports → Budgets → Budget Management Report → CSV).
              Multiple files can be selected at once.
            </div>
          </div>
          {canEdit && (
            <label className="inline-flex items-center gap-1.5 text-xs bg-gbe hover:bg-gbe/90 text-white rounded px-3 py-1.5 cursor-pointer"
                   data-testid={TIDS.budgetUploadBtn}>
              {uploading
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Upload className="w-3.5 h-3.5" />}
              Upload CSV(s)
              <input type="file" multiple accept=".csv,text/csv" className="hidden"
                     data-testid={TIDS.budgetUpload}
                     onChange={(e) => onUpload(e.target.files)} />
            </label>
          )}
        </div>
        {budgetSummaries.length > 0 && (
          <div className="border border-slate-200 rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                <tr>
                  <th className="text-left px-3 py-1.5">FY</th>
                  <th className="text-right px-3 py-1.5">Accounts</th>
                  <th className="text-right px-3 py-1.5">Months</th>
                  <th className="text-right px-3 py-1.5">Total budget</th>
                  <th className="text-right px-3 py-1.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {budgetSummaries.map((b) => (
                  <tr key={b.fy_year} className="border-b border-slate-100" data-testid={TIDS.budgetFY(b.fy_year)}>
                    <td className="px-3 py-1.5 font-medium">
                      <Badge className={`text-[10px] ${b.fy_year === fy ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                        {b.fy_label}
                      </Badge>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{b.accounts_count}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{b.months_count}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{fmt(b.total_amount)}</td>
                    <td className="px-3 py-1.5 text-right">
                      {canEdit && (
                        <button onClick={() => deleteFY(b.fy_year)}
                                data-testid={TIDS.budgetDelete(b.fy_year)}
                                className="text-rose-500 hover:text-rose-700">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {budgetSummaries.length === 0 && (
          <div className="text-[11px] text-slate-400 text-center py-4 border border-dashed border-slate-200 rounded">
            No budgets loaded yet — upload your first CSV above.
          </div>
        )}
      </div>

      {/* P&L Actuals upload strip */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
              <FileDown className="w-4 h-4" /> P&amp;L Actuals import
            </div>
            <div className="text-[11px] text-slate-500">
              Upload MYOB <b>Profit &amp; Loss</b> reports as <b>XLSX or CSV</b>. Accepts the default P&amp;L export (single-total column) or multi-period monthly reports.
              This unlocks accurate per-BU cost splits (Materials/Wages/Subcontractors) and real GM%.
            </div>
          </div>
          {canEdit && (
            <label className="inline-flex items-center gap-1.5 text-xs bg-gbe hover:bg-gbe/90 text-white rounded px-3 py-1.5 cursor-pointer"
                   data-testid={TIDS.actualsUploadBtn}>
              {uploadingActuals ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              Upload P&amp;L CSV/XLSX
              <input type="file" multiple accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden"
                     data-testid={TIDS.actualsUpload}
                     onChange={(e) => onUploadActuals(e.target.files)} />
            </label>
          )}
        </div>
        {actualsSummaries.length > 0 && (
          <div className="border border-slate-200 rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                <tr>
                  <th className="text-left px-3 py-1.5">FY</th>
                  <th className="text-right px-3 py-1.5">Accounts</th>
                  <th className="text-right px-3 py-1.5">Months</th>
                  <th className="text-right px-3 py-1.5">Actions</th>
                </tr>
              </thead>
              <tbody>
                {actualsSummaries.map((b) => (
                  <tr key={b.fy_year} className="border-b border-slate-100" data-testid={TIDS.actualsFY(b.fy_year)}>
                    <td className="px-3 py-1.5 font-medium">
                      <Badge className={`text-[10px] ${b.fy_year === fy ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                        {b.fy_label}
                      </Badge>
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{b.accounts_count}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{b.months_count}</td>
                    <td className="px-3 py-1.5 text-right">
                      {canEdit && (
                        <button onClick={() => deleteActualsFY(b.fy_year)}
                                data-testid={TIDS.actualsDelete(b.fy_year)}
                                className="text-rose-500 hover:text-rose-700">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {actualsSummaries.length === 0 && (
          <div className="text-[11px] text-slate-400 text-center py-4 border border-dashed border-slate-200 rounded">
            No P&amp;L actuals loaded yet — while blank, we approximate cost splits from account balances.
          </div>
        )}
      </div>
    </div>
  );
}



// ============================================================
// Classifier Diagnostics Modal
// Lists every cost-of-sales account for the selected FY and shows the bucket
// each was mapped to. Accounts under "other_cogs" are the culprits behind
// any $0 column (esp. Materials) — copy the JSON and share for patching.
// ============================================================
function ClassifierDiagnosticsModal({ fy, open, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) return;
    setBusy(true); setErr(null);
    api.get(`/myob/budgets/pl-summary/diagnostics?fy_year=${fy}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || e.message))
      .finally(() => setBusy(false));
  }, [open, fy]);

  if (!open) return null;

  const buckets = data?.by_bucket_total || {};
  const bucketOrder = ["materials", "direct_wages", "labour_hire", "subcontractors",
                       "opening_stock", "closing_stock", "other_cogs"];
  const otherAccts = (data?.accounts || []).filter((a) => a.bucket === "other_cogs");

  const copyJson = () => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    toast.success("Diagnostic JSON copied");
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-6"
         onClick={onClose}
         data-testid="pnl-diag-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mt-8 max-h-[90vh] flex flex-col overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <Stethoscope className="w-4 h-4 text-gbe" />
              Cost-of-Sales Classifier · FY{fy % 100}
            </h3>
            <p className="text-[10px] text-slate-500">Every MYOB CoS account and the bucket it was mapped to. `other_cogs` = unmatched.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={copyJson} disabled={!data}
                    data-testid="pnl-diag-copy">Copy JSON</Button>
            <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"
                    data-testid="pnl-diag-close">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {busy && <div className="text-xs text-slate-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>}
          {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">{err}</div>}

          {data && (
            <>
              <div>
                <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Totals by bucket</h4>
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                  {bucketOrder.map((b) => {
                    const val = buckets[b] || 0;
                    const isProblem = b === "other_cogs" && val > 0;
                    const isMissing = b === "materials" && val === 0;
                    return (
                      <div key={b}
                           className={`rounded border px-2 py-1.5 ${
                             isProblem ? "border-rose-300 bg-rose-50"
                             : isMissing ? "border-amber-300 bg-amber-50"
                             : "border-slate-200 bg-white"
                           }`}>
                        <div className="text-[10px] uppercase text-slate-500">{b.replace("_", " ")}</div>
                        <div className="text-sm font-semibold text-slate-800 tabular-nums">
                          {new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(val)}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {(buckets.other_cogs || 0) > 0 && (
                  <div className="mt-2 text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded p-2">
                    <b>{data.unclassified_count} account{data.unclassified_count === 1 ? "" : "s"} totalling {new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(buckets.other_cogs)}</b> weren't matched by any keyword. Scroll down to see them, then share the JSON so we can patch the classifier.
                  </div>
                )}
                {buckets.materials === 0 && (
                  <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                    <b>Materials = $0</b>. Look under "Unmatched accounts" below — those are almost certainly your materials accounts, we just need to add the right keyword.
                  </div>
                )}
              </div>

              {otherAccts.length > 0 && (
                <div>
                  <h4 className="text-[11px] font-semibold uppercase tracking-wider text-rose-700 mb-2">
                    Unmatched accounts ({otherAccts.length}) — likely your Materials / Purchases
                  </h4>
                  <div className="border border-rose-200 rounded overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-rose-50 text-[10px] uppercase text-rose-700 border-b border-rose-200">
                        <tr>
                          <th className="text-left px-3 py-1.5">Account No.</th>
                          <th className="text-left px-3 py-1.5">Account name</th>
                          <th className="text-right px-3 py-1.5">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {otherAccts.map((a, i) => (
                          <tr key={i} className="border-b border-rose-100">
                            <td className="px-3 py-1 font-mono text-[10px] text-slate-600">{a.account_no}</td>
                            <td className="px-3 py-1 font-medium">{a.account_name}</td>
                            <td className="px-3 py-1 text-right tabular-nums">
                              {new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(a.amount)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div>
                <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  All CoS accounts ({data.cogs_accounts_total})
                </h4>
                <div className="border border-slate-200 rounded overflow-hidden max-h-[280px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0 text-[10px] uppercase text-slate-500 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-3 py-1.5">No.</th>
                        <th className="text-left px-3 py-1.5">Account</th>
                        <th className="text-left px-3 py-1.5">Bucket</th>
                        <th className="text-right px-3 py-1.5">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.accounts.map((a, i) => (
                        <tr key={i} className={`border-b border-slate-100 ${a.bucket === "other_cogs" ? "bg-rose-50/40" : ""}`}>
                          <td className="px-3 py-1 font-mono text-[10px] text-slate-600">{a.account_no}</td>
                          <td className="px-3 py-1">{a.account_name}</td>
                          <td className="px-3 py-1 text-[10px] uppercase text-slate-500">{a.bucket.replace("_", " ")}</td>
                          <td className="px-3 py-1 text-right tabular-nums">
                            {new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(a.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ============================================================
// PLDrilldownModal
// Line-item drill-down for a single BU × month cell in the P&L Summary.
// Reads /api/finance/v1/pl-summary/drill and shows invoices + bills.
// ============================================================
function PLDrilldownModal({ target, fyMonths, onClose, onMonthChange }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!target?.bu_id || !target?.month) { setData(null); return; }
    setBusy(true); setErr(null);
    api.get("/finance/v1/pl-summary/drill", {
      params: { business_unit_id: target.bu_id, month: target.month, kind: "all" },
    })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || e.message))
      .finally(() => setBusy(false));
  }, [target?.bu_id, target?.month]);

  if (!target) return null;

  const fmtMoney = (n) => n == null ? "—" : "$" + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-6"
         onClick={onClose}
         data-testid="pnl-drill-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl mt-8 max-h-[90vh] flex flex-col overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">
              Drill-down · <span className="font-mono">{target.bu_code}</span>
              <span className="text-slate-400 ml-1">({target.bu_name})</span>
            </h3>
            <p className="text-[10px] text-slate-500">
              Underlying invoices & bills for this BU in the selected month.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {fyMonths?.length > 0 && (
              <select
                value={target.month || ""}
                onChange={(e) => onMonthChange(e.target.value)}
                data-testid="pnl-drill-month-select"
                className="text-xs border border-slate-200 rounded px-2 py-1 bg-white"
              >
                {fyMonths.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            )}
            <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700"
                    data-testid="pnl-drill-close" aria-label="Close">
              ✕
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {busy && <div className="text-xs text-slate-500">Loading…</div>}
          {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">{err}</div>}

          {data && (
            <>
              {/* Summary strip */}
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded border border-slate-200 bg-slate-50 p-3">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">BU Accounts</div>
                  <div className="text-lg font-semibold text-slate-800 mt-1">
                    {data.accounts_matched?.length || 0}
                  </div>
                  <div className="text-[10px] text-slate-500 truncate" title={(data.accounts_matched || []).join(", ")}>
                    {(data.accounts_matched || []).slice(0, 5).join(", ") || "(no BU-account mapping — showing all)"}
                    {(data.accounts_matched || []).length > 5 && "…"}
                  </div>
                </div>
                <div className="rounded border border-emerald-200 bg-emerald-50 p-3"
                     data-testid="pnl-drill-invoice-summary">
                  <div className="text-[10px] uppercase tracking-wide text-emerald-700">Invoices</div>
                  <div className="text-lg font-semibold text-emerald-900 mt-1">
                    {fmtMoney(data.invoices?.total)}
                  </div>
                  <div className="text-[10px] text-emerald-700">{data.invoices?.count || 0} lines</div>
                </div>
                <div className="rounded border border-rose-200 bg-rose-50 p-3"
                     data-testid="pnl-drill-bill-summary">
                  <div className="text-[10px] uppercase tracking-wide text-rose-700">Bills</div>
                  <div className="text-lg font-semibold text-rose-900 mt-1">
                    {fmtMoney(data.bills?.total)}
                  </div>
                  <div className="text-[10px] text-rose-700">{data.bills?.count || 0} lines</div>
                </div>
              </div>

              {/* Invoices table */}
              <div>
                <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  Invoices ({data.invoices?.items?.length || 0}
                  {data.invoices?.truncated ? " · truncated at 500" : ""})
                </h4>
                {(data.invoices?.items?.length || 0) === 0 ? (
                  <div className="text-[11px] text-slate-500 italic py-3">No invoices this month.</div>
                ) : (
                  <div className="border border-slate-200 rounded overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                        <tr>
                          <th className="text-left px-2 py-1">Date</th>
                          <th className="text-left px-2 py-1">Number</th>
                          <th className="text-left px-2 py-1">Customer</th>
                          <th className="text-left px-2 py-1">Description</th>
                          <th className="text-left px-2 py-1">Account</th>
                          <th className="text-right px-2 py-1">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.invoices.items.map((r) => (
                          <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                            <td className="px-2 py-1 text-slate-700">{r.date || "—"}</td>
                            <td className="px-2 py-1 font-mono text-slate-700">{r.number || "—"}</td>
                            <td className="px-2 py-1 text-slate-700">{r.customer_name || "—"}</td>
                            <td className="px-2 py-1 text-slate-600 truncate max-w-[240px]">{r.description || "—"}</td>
                            <td className="px-2 py-1 font-mono text-slate-500 text-[10px]">{r.account_no || "—"}</td>
                            <td className="px-2 py-1 text-right tabular-nums text-slate-800">{fmtMoney(r.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Bills table */}
              <div>
                <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
                  Bills ({data.bills?.items?.length || 0}
                  {data.bills?.truncated ? " · truncated at 500" : ""})
                </h4>
                {(data.bills?.items?.length || 0) === 0 ? (
                  <div className="text-[11px] text-slate-500 italic py-3">No bills this month.</div>
                ) : (
                  <div className="border border-slate-200 rounded overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                        <tr>
                          <th className="text-left px-2 py-1">Date</th>
                          <th className="text-left px-2 py-1">Number</th>
                          <th className="text-left px-2 py-1">Supplier</th>
                          <th className="text-left px-2 py-1">Description</th>
                          <th className="text-left px-2 py-1">Account</th>
                          <th className="text-right px-2 py-1">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.bills.items.map((r) => (
                          <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                            <td className="px-2 py-1 text-slate-700">{r.date || "—"}</td>
                            <td className="px-2 py-1 font-mono text-slate-700">{r.number || "—"}</td>
                            <td className="px-2 py-1 text-slate-700">{r.supplier_name || "—"}</td>
                            <td className="px-2 py-1 text-slate-600 truncate max-w-[240px]">{r.description || "—"}</td>
                            <td className="px-2 py-1 font-mono text-slate-500 text-[10px]">{r.account_no || "—"}</td>
                            <td className="px-2 py-1 text-right tabular-nums text-slate-800">{fmtMoney(r.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
